import { Button } from '@mui/material'
import type { TFunction } from 'i18next'
import type { PopoverProps as AriaPopoverProps } from 'react-aria-components'
import {
  Dialog as AriaDialog,
  OverlayArrow as AriaOverlayArrow,
  Popover as AriaPopover,
} from 'react-aria-components'
import { UseState } from 'src/components/Common/UseState'
import { UIDialog } from 'src/components/ui/Modal'
import type { Artwork } from 'src/services/apollo/generated/graphql'
import { cx } from 'src/utils/cx'
import { dynamicCreateElement } from 'src/utils/dynamic'
import { useIsMobile } from 'src/utils/hooks/useBreakpoint'
import ArtworkShareContent, {
  type ArtworkForShare,
} from './ArtworkShareContent'
import type { ArtworkShareActionProps } from './ArtworkShareDialogActions'

// ---------------------------------------------------------------------------
// openShareArtworkDialog (imperative, non-React)
// ---------------------------------------------------------------------------

export const openShareArtworkDialog = (
  artwork: ArtworkForShare,
  opts?: ArtworkShareActionProps['opts'],
) => {
  return new Promise<void>(resolve => {
    const destroy = dynamicCreateElement(
      <UseState initial={true}>
        {(open, setOpen) => {
          const onClose = () => {
            resolve()
            setOpen(false)
          }
          return (
            <UIDialog
              open={open}
              onClose={onClose}
              dismissable
              onExitComplete={() => {
                destroy()
              }}
              className="max-w-[400px]"
            >
              <ArtworkShareContent
                artwork={artwork}
                scene={opts?.trackingArgs?.scene}
                onClose={onClose}
              />
            </UIDialog>
          )
        }}
      </UseState>,
    )
  })
}

// ---------------------------------------------------------------------------
// ArtworkSharePopover (React component)
// ---------------------------------------------------------------------------

export function ArtworkSharePopover(
  props: Pick<
    AriaPopoverProps,
    'onOpenChange' | 'triggerRef' | 'className' | 'style'
  > & {
    open: AriaPopoverProps['isOpen']
    artwork: ArtworkForShare
    trackingArgs?: Record<string, any>
  },
) {
  const isMobile = useIsMobile()
  const onClose = () => props.onOpenChange?.(false)

  if (isMobile) {
    return (
      <UIDialog
        open={props.open}
        onClose={onClose}
        dismissable
        className="max-w-[400px]"
      >
        <ArtworkShareContent
          artwork={props.artwork}
          scene={props.trackingArgs?.scene}
          onClose={onClose}
        />
      </UIDialog>
    )
  }

  return (
    <AriaPopover
      isOpen={props.open}
      onOpenChange={props.onOpenChange}
      triggerRef={props.triggerRef}
      placement="top"
      containerPadding={16}
      className={cx(
        'text-sm bg-white dark:bg-zinc-700 border border-black/10 dark:border-white/10 rounded-2xl shadow-lg',
        props.className,
      )}
      style={props.style}
    >
      <AriaOverlayArrow className="group">
        <svg
          width={12}
          height={6}
          viewBox="0 0 12 6"
          className="stroke-black/10 dark:stroke-white/10 fill-white dark:fill-zinc-700 group-data-[placement=bottom]:rotate-180"
        >
          <path d="M0 0 L6 6 L12 0" />
        </svg>
      </AriaOverlayArrow>
      <AriaDialog className="outline-none">
        <ArtworkShareContent
          artwork={props.artwork}
          scene={props.trackingArgs?.scene}
          onClose={onClose}
        />
      </AriaDialog>
    </AriaPopover>
  )
}

// ---------------------------------------------------------------------------
// Success dialog (shared)
// ---------------------------------------------------------------------------

export function openArtworkShareActionSuccessDialog(
  t: TFunction<('artwork' | 'action')[]>,
  opts: { type: 'copy' },
) {
  return UIDialog.create({
    children: ({ close }) => (
      <div className="py-4 px-8 flex flex-col justify-center items-center">
        <p className="text-xl text-center">
          {t('artwork:share.watermark-dialog.copy-modal.title')}
        </p>
        <Button
          variant="contained"
          className="text-white bg-theme-primary mt-8 px-12 !font-bold"
          onClick={() => {
            close()
          }}
        >
          {t('action:ok')}
        </Button>
      </div>
    ),
  })
}
